import { colorPalette } from '@sharefiledev/antd-config';
import { CurrencyIcon, PaperclipIcon } from '@sharefiledev/flow-web';
import {
	CalendarMark,
	CheckSquare,
	DropdownMenu,
	Initials,
	MultiSelect,
	Signature,
	TextEditingTyping,
} from '@sharefiledev/icons';
import {
	ComponentInputType,
	DataPreFilledType,
	PAGE_WIDTH_THRESHOLD,
} from '../../constants';
import { t } from '../../utils';
import { hexToRgb } from '../../utils/colour';
import {
	Component,
	ComponentTypes,
	Field,
	GroupRequirement,
	TextAlignment,
} from './types';

const DEFAULT_WORKFLOWS = [
	'reusableTemplate',
	'signatureRequest',
	'inPerson',
	'packager',
];

const AVAILABLE_ATTRIBUTES = [
	{ name: 'name', workflows: DEFAULT_WORKFLOWS },
	{ name: 'assignedTo', workflows: DEFAULT_WORKFLOWS },
	{ name: 'isRequired', workflows: DEFAULT_WORKFLOWS },
	{ name: 'fontSize', workflows: DEFAULT_WORKFLOWS.concat('selfSign') },
	{ name: 'multiline', workflows: DEFAULT_WORKFLOWS.concat('selfSign') },
	{ name: 'transparent', workflows: DEFAULT_WORKFLOWS.concat('selfSign') },
	{ name: 'groupRequirement', workflows: DEFAULT_WORKFLOWS },
	{ name: 'value', workflows: DEFAULT_WORKFLOWS },
	{ name: 'textAlign', workflows: DEFAULT_WORKFLOWS.concat('selfSign') },
	{ name: 'dateFormat', workflows: DEFAULT_WORKFLOWS },
	{ name: 'selectOptions', workflows: DEFAULT_WORKFLOWS },
	{ name: 'chargeAfterExecuted', workflows: DEFAULT_WORKFLOWS },
	{ name: 'paymentAmount', workflows: DEFAULT_WORKFLOWS },
	{ name: 'helpText', workflows: DEFAULT_WORKFLOWS },
	{ name: 'isMergeField', workflows: ['reusableTemplate'] },
	{ name: 'isDateSigned', workflows: DEFAULT_WORKFLOWS },
	{ name: 'checkboxes', workflows: DEFAULT_WORKFLOWS },
	{ name: 'paymentType', workflows: DEFAULT_WORKFLOWS },
	{ name: 'dataSource', workflows: DEFAULT_WORKFLOWS },
	{ name: 'recordType', workflows: DEFAULT_WORKFLOWS },
];

const COMPONENT_ATTRIBUTES: Record<ComponentTypes, string[]> = {
	SignatureComponent: ['name', 'assignedTo', 'isRequired', 'transparent'],
	TextComponent: [
		'name',
		'assignedTo',
		'isRequired',
		'multiline',
		'transparent',
		'textAlign',
		'value',
		'isMergeField',
	],
	DateComponent: [
		'name',
		'assignedTo',
		'isRequired',
		'transparent',
		'textAlign',
		'dateFormat',
		'isMergeField',
		'isDateSigned',
	],
	CheckComponent: ['name', 'assignedTo', 'isRequired', 'transparent', 'isMergeField'],
	CheckGroupComponent: [
		'name',
		'assignedTo',
		'isRequired',
		'transparent',
		'groupRequirement',
		'isMergeField',
		'checkboxes',
	],
	InitialsComponent: ['name', 'assignedTo', 'isRequired', 'transparent', 'textAlign'],
	SelectComponent: [
		'name',
		'assignedTo',
		'isRequired',
		'transparent',
		'selectOptions',
		'isMergeField',
	],
	PaymentComponent: ['name', 'assignedTo', 'isRequired', 'value', 'helpText'],
	AttachmentComponent: ['name', 'assignedTo', 'isRequired', 'transparent', 'helpText'],
	CheckmarkAnnotationComponent: [
		'name',
		'assignedTo',
		'isRequired',
		'transparent',
		'isMergeField',
	],
	TextAnnotationComponent: ['assignedTo', 'fontSize'],
	SignatureAnnotationComponent: ['name', 'assignedTo', 'isRequired', 'transparent'],
};

const SYSTEM_FILLED_COMPONENT_ATTRIBUTES: Record<ComponentTypes, string[]> = {
	...COMPONENT_ATTRIBUTES,
	TextComponent: ['name', 'dataSource', 'recordType'],
};

export const OFFSET_X = 10; // Minimum margin on right end of the document. Required to avail some space for mandatory field indicator *

export function getAllFields(): Field[] {
	return [
		{
			type: ComponentTypes.SignatureComponent,
			label: t('signature'),
			icon: Signature,
		},
		{
			type: ComponentTypes.InitialsComponent,
			label: t('initials'),
			icon: Initials,
		},
		{
			type: ComponentTypes.DateComponent,
			label: t('date'),
			icon: CalendarMark,
		},
		{
			type: ComponentTypes.TextComponent,
			label: t('text'),
			icon: TextEditingTyping,
		},
		{
			type: ComponentTypes.SelectComponent,
			label: t('SelectFieldLabel'),
			icon: DropdownMenu,
		},
		{
			type: ComponentTypes.CheckComponent,
			label: t('checkbox'),
			icon: CheckSquare,
		},
		{
			type: ComponentTypes.PaymentComponent,
			label: t('PaymentFieldLabel'),
			icon: CurrencyIcon,
		},
		{
			type: ComponentTypes.AttachmentComponent,
			label: t('components.attachment'),
			icon: PaperclipIcon,
		},
		{
			type: ComponentTypes.SignatureAnnotationComponent,
			label: t('signature'),
			icon: Signature,
		},
		{
			type: ComponentTypes.TextAnnotationComponent,
			label: t('text'),
			icon: TextEditingTyping,
		},
		{
			type: ComponentTypes.CheckmarkAnnotationComponent,
			label: t('checkbox'),
			icon: CheckSquare,
		},
		{
			type: ComponentTypes.CheckGroupComponent,
			label: t('checkboxGroup'),
			icon: MultiSelect,
		},
	];
}

export function getEditableAttributes(
	componentType: ComponentTypes,
	workflow: string,
	isSourceMapped?: boolean
): string[] {
	const editableAttributes: string[] = isSourceMapped
		? SYSTEM_FILLED_COMPONENT_ATTRIBUTES[componentType]
		: COMPONENT_ATTRIBUTES[componentType];
	return editableAttributes.filter(attr => {
		const attrWorkflows = AVAILABLE_ATTRIBUTES.find(a => a.name === attr).workflows;
		return attrWorkflows.includes(workflow);
	});
}

export function getDefaultComponent(componentType: ComponentTypes): Component {
	const component = {} as Component;
	component.isRequired = true;
	component.groupRequirement = GroupRequirement.OnlyOne;
	component.fontSize = 14;
	component.multiline = false;
	component.transparent = true;
	component.textAlign = TextAlignment.Left;
	component.selectOptions = ['Option 1', 'Option 2'];
	component.chargeAfterExecuted = false;
	component.isMergeField = false;
	component.dateFormat = 'MM/DD/YYYY';
	component.isAnnotation = false;

	if (componentType === ComponentTypes.SelectComponent) {
		component.showErrorOnResizable = false;
	}

	if (componentType === ComponentTypes.CheckComponent) {
		component.isRequired = false;
	}

	return component;
}

export function getDefaultComponentNames(componentType: ComponentTypes): string {
	const translationLabel = componentType.replace('Component', 'FieldLabel');
	return t(translationLabel);
}

export function isAnnotationComponent(componentType: ComponentTypes): boolean {
	return (
		componentType === ComponentTypes.TextAnnotationComponent ||
		componentType === ComponentTypes.SignatureAnnotationComponent ||
		componentType === ComponentTypes.CheckmarkAnnotationComponent
	);
}

const SEQUENCE_COLOUR_NAMES = [
	'blue',
	'orange',
	'teal',
	'purple',
	'yellow',
	'green',
	'skyblue',
	'maroon',
	'pine',
	'lavender',
	'seagreen',
	'peach',
	'greyscale',
];

const SEQUENCE_COLOUR_HEXCODES = [
	'#0045DB',
	'#F96800',
	'#1C9CAD',
	'#691CC2',
	'#D69600',
	'#4A9912',
	'#3894FF',
	'#A34400',
	'#005463',
	'#8F3BF2',
	'#36C2CC',
	'#FF7373',
	'#171717',
];

export const SIGNER_COLORS = SEQUENCE_COLOUR_NAMES.map((name, index) => {
	return { name, color: SEQUENCE_COLOUR_HEXCODES[index] };
});

export const MERGE_FIELD_STATES = {
	unfilled: {
		name: 'neutral',
		color: colorPalette.neutral13,
	},
	filled: {
		name: 'emerald',
		color: colorPalette.green1,
	},
};

export const colourCount = SIGNER_COLORS.length;

export const FieldColors = Object.assign(
	{},
	...SEQUENCE_COLOUR_NAMES.map((name, index) => {
		return { [name]: SEQUENCE_COLOUR_HEXCODES[index] };
	})
);

export const Direction = {
	Top: 'n',
	Left: 'w',
	Right: 'e',
	Bottom: 's',
	TopLeft: 'nw',
	TopRight: 'ne',
	BottomLeft: 'sw',
	BottomRight: 'se',
} as const;

export const defaultComponentDimensions: Record<
	ComponentTypes,
	{
		width: number;
		height: number;
		minWidth: number;
		minHeight: number;
		labelThresholdWidth: number;
		aspectRatio: number;
		lockAspectRatio: boolean;
	}
> = {
	CheckComponent: {
		width: 24,
		height: 24,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 24,
		aspectRatio: 1,
		lockAspectRatio: true,
	},
	CheckGroupComponent: {
		width: 24,
		height: 24,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 24,
		aspectRatio: 1,
		lockAspectRatio: true,
	},
	CheckmarkAnnotationComponent: {
		width: 24,
		height: 24,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 24,
		aspectRatio: 1,
		lockAspectRatio: true,
	},
	SignatureComponent: {
		width: 256,
		height: 64,
		minWidth: 96,
		minHeight: 24,
		labelThresholdWidth: 132,
		aspectRatio: 4,
		lockAspectRatio: true,
	},
	SignatureAnnotationComponent: {
		width: 256,
		height: 64,
		minWidth: 96,
		minHeight: 24,
		labelThresholdWidth: 167,
		aspectRatio: 4,
		lockAspectRatio: true,
	},
	TextComponent: {
		width: 296,
		height: 37,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 77,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
	TextAnnotationComponent: {
		width: 148,
		height: 18.5,
		minWidth: 1,
		minHeight: 1,
		labelThresholdWidth: 24,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
	SelectComponent: {
		width: 160,
		height: 40,
		minWidth: 96,
		minHeight: 24,
		labelThresholdWidth: 130,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
	DateComponent: {
		width: 160,
		height: 37,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 112,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
	InitialsComponent: {
		width: 99,
		height: 36,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 98,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
	AttachmentComponent: {
		width: 140,
		height: 40,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 130,
		aspectRatio: 3.5,
		lockAspectRatio: false,
	},
	PaymentComponent: {
		width: 296,
		height: 37,
		minWidth: 24,
		minHeight: 24,
		labelThresholdWidth: 77,
		aspectRatio: 4,
		lockAspectRatio: false,
	},
};

export const defaultCheckBoxAnnotationIconSize = 13;

export type DirectionType = keyof typeof Direction;
export type DirectionValueType = (typeof Direction)[DirectionType];

export const defaultComponentHandles: Record<ComponentTypes, DirectionValueType[]> = {
	CheckComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	CheckGroupComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	SignatureComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	TextComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	SelectComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	DateComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	InitialsComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	AttachmentComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	PaymentComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	CheckmarkAnnotationComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	SignatureAnnotationComponent: [
		Direction.TopLeft,
		Direction.BottomLeft,
		Direction.BottomRight,
		Direction.TopRight,
	],
	TextAnnotationComponent: [],
};
export const defaultWidth = 1100;
export const defaultHeight = 1424;
export const minTextAnnotationWidth = 8;
export const PADDING = 20;
export const MARGIN_BOTTOM = 10;

const getHeightAndWidth = (string, fontSize) => {
	const element = document.createElement('span');
	element.style.fontFamily = 'Courier, sans-serif';
	element.style.fontSize = fontSize + 'px';
	element.style.position = 'absolute';

	document.body.appendChild(element);
	element.innerHTML = string;
	const textWidth = Math.ceil(element.offsetWidth);
	const textHeight = Math.ceil(element.offsetHeight);
	document.body.removeChild(element);
	return {
		width: textWidth,
		height: textHeight,
	};
};

export const sanitizeValueText = (val, availableWidth, fontSize) => {
	const allLines = val.split('\n');
	const sanitizedValue = [];
	for (let i = 0; i < allLines.length; i++) {
		const line = allLines[i];
		let potentialLine = '';
		const sanitizeString = [];
		for (let j = 0; j < line.length; j++) {
			const char = line[j];
			potentialLine = potentialLine + char;
			const potentialHeightAndWidth = getHeightAndWidth(potentialLine, fontSize);
			if (potentialHeightAndWidth['width'] >= availableWidth - PAGE_WIDTH_THRESHOLD) {
				sanitizeString.push(potentialLine.slice(0, -1));
				sanitizeString.push('\n');
				potentialLine = char;
			}
		}
		if (!!potentialLine) {
			sanitizeString.push(potentialLine);
		}
		sanitizedValue.push(sanitizeString.join(''));
	}
	return sanitizedValue.join('\n');
};

// eslint-disable-next-line @typescript-eslint/no-shadow
export const getParentHeightAndWidth = pageID => {
	const parentPos = getEditFlowElementById(pageID)?.getBoundingClientRect();
	const parentWidth = parentPos?.width ? parentPos.width : defaultWidth;
	const parentHeight = parentPos?.height ? parentPos.height : defaultHeight;

	return { parentWidth, parentHeight };
};

export const pageID = component => `page-${component.page ? component.page : 1}`;

export function getSignerColor(sequence: number | null) {
	return typeof sequence === 'number'
		? Object.values(FieldColors)[sequence % (colourCount - 1)]
		: FieldColors['greyscale'];
}

const signerComponents = new Set([
	'SignatureComponent',
	'TextComponent',
	'InitialsComponent',
	'DateComponent',
	'SelectComponent',
	'CheckComponent',
	'AttachmentComponent',
	'CheckGroupComponent',
]);

export const getSignerFields = () => {
	return getAllFields().filter(field => signerComponents.has(field.type));
};

export const getAnnotationFields = () => {
	return getAllFields().filter(field => field.type.includes('Annotation'));
};

export const colorMap = {
	blue: {
		backgroundColor: 'rgba(0, 69, 219, 0.1)',
		borderColor: '#0045db',
	},
	teal: {
		backgroundColor: 'rgba(28, 156, 173, 0.1)',
		borderColor: '#005463',
	},
	purple: {
		backgroundColor: 'rgba(105, 28, 194, 0.1)',
		borderColor: '#691cc2',
	},
	green: {
		backgroundColor: 'rgba(74, 153, 18, 0.1)',
		borderColor: '#61b024',
	},
	yellow: {
		backgroundColor: 'rgba(214, 150, 0, 0.1)',
		borderColor: '#d69600',
	},
	orange: {
		backgroundColor: 'rgba(249, 104, 0, 0.1)',
		borderColor: '#d15700',
	},
	skyblue: {
		backgroundColor: 'rgba(56, 148, 255, 0.1)',
		borderColor: '#3894ff',
	},
	maroon: {
		backgroundColor: 'rgba(163, 68, 0, 0.1)',
		borderColor: '#a34400',
	},
	pine: {
		backgroundColor: 'rgba(0, 84, 99, 0.1)',
		borderColor: '#005463',
	},
	lavender: {
		backgroundColor: 'rgba(143, 59, 242, 0.1)',
		borderColor: '#8f3bf2',
	},
	seagreen: {
		backgroundColor: 'rgba(54, 194, 204, 0.1)',
		borderColor: '#36c2cc',
	},
	peach: {
		backgroundColor: 'rgba(255, 115, 115, 0.1)',
		borderColor: '#ff7373',
	},
	greyscale: {
		backgroundColor: 'rgba(23, 23, 23, 0.1)',
		borderColor: '#171717',
	},
	neutral: {
		backgroundColor: `rgba(${hexToRgb(colorPalette.neutral13)}, 0.1)`,
		borderColor: colorPalette.neutral13,
	},
	emerald: {
		backgroundColor: `rgba(${hexToRgb(colorPalette.green8)}, 0.1)`,
		borderColor: colorPalette.green8,
	},
};

export const DEFAULT_GAP_CHECKBOXES = 4;

export const defaultLableFontSize = 18;
export const PreparePageModalMaxHeight = '400px';

export const getComponentType = component => {
	if (component.dataPreFilledType === DataPreFilledType.SourceMapped) {
		return ComponentInputType.System;
	} else if (isAnnotationComponent(component.type)) {
		return ComponentInputType.Annotation;
	} else {
		return ComponentInputType.Signer;
	}
};

export const docgenFullScreenEditFlowId = 'sf-doc-gen-pilet-full-screen-editable-flow';

export const getEditFlowElementById = (id: any) => {
	return window.document.querySelector(`#${docgenFullScreenEditFlowId} #${id}`);
};

export const createDrawerBaseClassName = 'sf-modal-docgen-pilet-create-drawer';
export const detailsDrawerBaseClassName = 'sf-modal-docgen-pilet-details-drawer';
export const signatureRequestDrawerBaseClassName =
	'sf-modal-docgen-pilet-signature-request-drawer';

export const getDrawerBodyElementSelector = (baseClassName: string) => {
	return `.${baseClassName} .ant-drawer-body`;
};

export const CREATE_TEMPLATE_HEADER_HEIGHT = '57px';
export const CREATE_TEMPLATE_FOOTER_HEIGHT = '57px';
